import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { History, Download, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import type { Project } from "@shared/schema";
import type { StoredQuoteData, StoredQuoteVersion } from "@/lib/storage";
import { buildVersionQuoteData } from "@/lib/quote-version";
import { PDFGenerator, DEFAULT_PDF_LAYOUT, type QuoteData } from "@/lib/pdf-generator";
import { formatDecimal } from "@/lib/formatters";

/**
 * Badge "N Versionen" für Projektlisten. Erscheint nur, wenn das Projekt
 * archivierte frühere Angebots-Versionen hat (`quoteData.versions`).
 * Klick öffnet einen Dialog mit allen Versionen (Nummer, Datum, Positionen,
 * Nettosumme) und Direkt-Export als PDF aus den eingefrorenen Daten.
 */
export function QuoteVersionsBadge({ project }: { project: Project }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [exportingKey, setExportingKey] = useState<string | null>(null);

  // quoteData ist eine jsonb-Spalte — der Inhalt kann aus alten Ständen oder
  // Importen stammen. Deshalb hier ein Runtime-Guard statt blindem Cast:
  // ungültige/unvollständige Versions-Einträge werden still ausgefiltert,
  // damit ein korrupter Eintrag nicht die ganze Projekt-Übersicht crasht.
  const versions = useMemo<StoredQuoteVersion[]>(() => {
    const qd = project.quoteData as Partial<StoredQuoteData> | null | undefined;
    if (!qd || typeof qd !== "object" || !Array.isArray(qd.versions)) return [];
    return qd.versions.filter((v): v is StoredQuoteVersion => {
      if (!v || typeof v !== "object") return false;
      const ver = v as Partial<StoredQuoteVersion>;
      return (
        typeof ver.quoteNumber === "string" &&
        typeof ver.archivedAt === "string" &&
        !!ver.snapshot &&
        typeof ver.snapshot === "object" &&
        !!ver.snapshot.customerData &&
        typeof ver.snapshot.customerData === "object" &&
        (ver.frozenItems === undefined || Array.isArray(ver.frozenItems))
      );
    });
  }, [project.quoteData]);

  const currentQuoteNumber = useMemo(() => {
    const qd = project.quoteData as Partial<StoredQuoteData> | null | undefined;
    return qd && typeof qd.quoteNumber === "string" ? qd.quoteNumber : null;
  }, [project.quoteData]);

  if (versions.length === 0) return null;

  const totalVersions = versions.length + 1; // + aktueller Stand

  const exportVersionPDF = async (version: StoredQuoteVersion) => {
    const key = `${version.quoteNumber}-${version.archivedAt}`;
    if ((version.frozenItems?.length ?? 0) === 0) {
      toast({
        title: "Keine Positionen",
        description: "Für diese Version wurden keine eingefrorenen Positionen gespeichert.",
        variant: "destructive",
      });
      return;
    }
    setExportingKey(key);
    try {
      const data = buildVersionQuoteData(version, project.name) as QuoteData;
      const pdfBlob = await new PDFGenerator().generateQuotePDF(data, { ...DEFAULT_PDF_LAYOUT });
      const url = URL.createObjectURL(pdfBlob);
      const safeName = (version.snapshot.customerData.name || "Kunde").replace(/\s+/g, "_");
      const a = document.createElement("a");
      a.href = url;
      a.download = `Angebot_${version.quoteNumber}_${safeName}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast({
        title: "PDF erstellt",
        description: `Version "${version.quoteNumber}" wurde als PDF heruntergeladen.`,
      });
    } catch (err) {
      console.error("Fehler bei Version-PDF-Export aus der Übersicht:", err);
      toast({
        title: "PDF Fehler",
        description: "Das PDF dieser Version konnte nicht erstellt werden.",
        variant: "destructive",
      });
    } finally {
      setExportingKey(null);
    }
  };

  return (
    <>
      <Badge
        variant="outline"
        className="border-bauwerk-teal/40 text-bauwerk-teal bg-bauwerk-mint/30 hover:bg-bauwerk-mint cursor-pointer flex items-center gap-1"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        data-testid={`badge-quote-versions-${project.id}`}
        title="Angebots-Versionen anzeigen"
      >
        <History className="w-3 h-3" />
        {totalVersions} Versionen
      </Badge>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <DialogHeader>
            <DialogTitle className="text-bauwerk-teal flex items-center gap-2">
              <History className="w-4 h-4" />
              Angebots-Versionen
            </DialogTitle>
            <p className="text-xs text-muted-foreground">{project.name}</p>
          </DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {currentQuoteNumber && (
              <div className="flex items-center justify-between border border-bauwerk-teal/30 bg-bauwerk-mint/20 rounded-md px-3 py-2 text-sm">
                <div>
                  <span className="font-semibold text-bauwerk-teal">{currentQuoteNumber}</span>
                  <span className="ml-2 text-xs text-muted-foreground">Aktueller Stand</span>
                </div>
                <Badge className="bg-bauwerk-teal text-white">Aktuell</Badge>
              </div>
            )}
            {[...versions].reverse().map((v, idx) => {
              const key = `${v.quoteNumber}-${v.archivedAt}`;
              const isExporting = exportingKey === key;
              return (
                <div
                  key={key}
                  className="flex items-center justify-between border rounded-md px-3 py-2 text-sm"
                  data-testid={`overview-quote-version-${project.id}-${idx}`}
                >
                  <div className="min-w-0">
                    <span className="font-semibold text-bauwerk-teal">{v.quoteNumber}</span>
                    <p className="text-xs text-muted-foreground">
                      gesichert am {new Date(v.archivedAt).toLocaleDateString("de-DE")}
                      {(v.frozenItems?.length ?? 0) > 0 && ` · ${v.frozenItems!.length} Pos.`}
                      {typeof v.frozenNetTotal === "number" &&
                        ` · ${formatDecimal(v.frozenNetTotal, 2)} € netto`}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="shrink-0 ml-3"
                    disabled={isExporting}
                    onClick={() => exportVersionPDF(v)}
                    data-testid={`button-export-version-pdf-${project.id}-${idx}`}
                  >
                    {isExporting ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Download className="w-3.5 h-3.5" />
                    )}
                    <span className="ml-1.5">PDF</span>
                  </Button>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
